import { useLocation, useNavigate } from 'react-router-dom';
import { ControlledTabs } from '@/components/ui';
import { useSpaceContext } from '@/contexts/SpaceContext';
import { meetsRole } from '@/lib/space-roles';
import { routes } from '@/lib/routes';
import { GeneralTab } from './GeneralTab';
import { MembersTab } from './MembersTab';
import { AutoJoinRulesTab } from './AutoJoinRulesTab';
import { LabelsTab } from './LabelsTab';

const TABS_NON_PERSONAL = [
  { id: 'general', label: 'General' },
  { id: 'members', label: 'Members' },
  { id: 'auto-join', label: 'Auto-join' },
  { id: 'labels', label: 'Labels' },
];

const TABS_PERSONAL = [
  { id: 'general', label: 'General' },
  { id: 'labels', label: 'Labels' },
];

export default function SpaceManagePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { spaceId, role, isPersonal, space } = useSpaceContext();

  const tabs = isPersonal ? TABS_PERSONAL : TABS_NON_PERSONAL;

  // Members and Auto-join are admin+ only on the backend (the call 403s
  // otherwise). Labels reads/writes are viewer+/member+ so the tab stays
  // visible to everyone.
  const showAdminTabs = !isPersonal && meetsRole(role, 'admin');
  const visibleTabs = showAdminTabs
    ? tabs
    : tabs.filter((t) => t.id !== 'members' && t.id !== 'auto-join');

  const activeTab = location.pathname.endsWith('/space/members')
    ? 'members'
    : location.pathname.endsWith('/space/auto-join')
      ? 'auto-join'
      : location.pathname.endsWith('/space/labels')
        ? 'labels'
        : 'general';

  const setActiveTab = (tab: string) => {
    const target =
      tab === 'members'
        ? routes.console.spaceMembers(spaceId)
        : tab === 'auto-join'
          ? routes.console.spaceAutoJoin(spaceId)
          : tab === 'labels'
            ? routes.console.spaceLabels(spaceId)
            : routes.console.spaceManage(spaceId);
    navigate(target, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {space?.name ?? 'Space'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage this space's details and members.
        </p>
      </div>

      <ControlledTabs tabs={visibleTabs} activeTab={activeTab} onChange={setActiveTab}>
        {activeTab === 'general' && <GeneralTab />}
        {activeTab === 'members' && showAdminTabs && <MembersTab />}
        {activeTab === 'auto-join' && showAdminTabs && <AutoJoinRulesTab />}
        {activeTab === 'labels' && <LabelsTab />}
      </ControlledTabs>
    </div>
  );
}
